import { createGlobalStyle } from "styled-components"

// ============================================================
// DYNAMIC WEB PARTNERS — global styles
//
// Dark & cinematic with a single amber "spotlight" accent.
// Tokens live here as :root custom properties so both the new
// var(--color-…) styles and the original var(--primary) /
// var(--background) styles resolve to the amber-on-warm-black
// system. Legacy tokens at the bottom of :root are bridged.
// ============================================================

export const GlobalStyle = createGlobalStyle`
  :root {
    color-scheme: dark;

    /* ── surfaces (warm near-black) ── */
    --color-ink: #0b0a08;
    --color-ink-pure: #000000;
    --color-coal: #16130d;
    --color-panel: #1c1711;
    --color-smoke: #221c12;
    --color-line: #2f2718;

    /* ── text ── */
    --color-paper: #f6f3ea;
    --color-ash: #a79d89;
    --color-faint: #6f6553;

    /* ── the one accent: amber "spotlight" ── */
    --color-gold-700: #a86f1f;
    --color-gold-500: #e7a338;
    --color-gold-400: #ffc35c;
    --color-glow: rgba(231, 163, 56, 0.16);

    /* ── type families ── */
    --font-display: "Syne", system-ui, sans-serif; /* all display text + headings */
    --font-sans: "Satoshi", "Inter", system-ui, -apple-system, sans-serif;
    --font-mono: var(--font-sans); /* safety alias — no monospace in this system */

    /* ── fluid type scale ── */
    --text-mega: clamp(3rem, 9.2vw, 11rem);
    --text-headline: clamp(2.25rem, 6vw, 5.75rem);
    --text-title: clamp(1.6rem, 3vw, 2.85rem);
    --text-lead: clamp(1.1rem, 1.6vw, 1.5rem);
    --text-kicker: 0.8rem;
    --text-eyebrow: var(--text-kicker); /* legacy alias */

    /* ── motion ── */
    --ease-expo: cubic-bezier(0.16, 1, 0.3, 1);
    --ease-power: cubic-bezier(0.7, 0, 0.2, 1);

    /* ── legacy tokens, repointed at the amber palette ── */
    --background: var(--color-ink);     /* was #1b1b1b */
    --primary: var(--color-gold-500);   /* was #ffc400 (yellow) */
    --bodyColor: var(--color-ash);
    --inActive: var(--color-faint);
    --letterSpacing: -0.02em;
    --boxShadow: 0 24px 60px -10px rgba(0, 0, 0, 0.7);
    --textShadow: 0 2px 26px rgba(0, 0, 0, 0.7);

    /* ── legacy spacing / sizing (layout relies on these) ── */
    --borderSpacing: 24px;
    --menuWidth: 100vw;
    --blockquote: 20px;
    --p: 15px;
    --h2: 20px;
    --h3: 19px;
    --h4: 18px;
    --h5: 17px;
    --h6: 16px;
    --bannerTitle: 40px;
    --gap: 40px;
    --sectionMargin: 70px;

    @media (min-width: 375px) {
      --h2: 23px; --h3: 21px; --h4: 20px; --h5: 19px; --h6: 18px;
      --bannerTitle: 48px; --sectionMargin: 90px;
    }
    @media (min-width: 414px) {
      --h2: 27px; --h3: 25px; --h4: 23px; --h5: 21px; --h6: 20px;
      --bannerTitle: 56px;
    }
    @media (min-width: 768px) {
      --borderSpacing: 48px; --blockquote: 26px; --p: 16px;
      --h2: 34px; --h3: 28px; --h4: 24px; --h5: 22px; --h6: 20px;
      --bannerTitle: 76px; --sectionMargin: 120px;
    }
    @media (min-width: 1024px) {
      --borderSpacing: 72px; --p: 16px;
      --h2: 44px; --h3: 34px; --h4: 27px; --h5: 24px; --h6: 22px;
      --bannerTitle: 102px; --sectionMargin: 150px;
    }
    @media (min-width: 1200px) {
      --p: 17px; --borderSpacing: 88px;
    }
  }

  *, *::before, *::after {
    box-sizing: border-box;
  }

  html {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    scroll-behavior: smooth;
  }

  body {
    margin: 0;
    background-color: var(--color-ink);
    color: var(--color-paper);
    font-family: var(--font-sans);
    font-size: var(--p);
    line-height: 1.6;
    overflow-x: hidden;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-display);
    font-weight: 700;
    line-height: 1.05;
    letter-spacing: -0.01em;
    text-wrap: balance;
    color: var(--color-paper);
  }

  h1, h2 {
    margin-top: 0.5rem;
    margin-bottom: 1rem;

    @media (min-width: 1200px) {
      margin-bottom: 1.5rem;
    }
  }

  h3, h4, h5, h6 {
    margin-top: 0.5rem;
    margin-bottom: 1rem;
  }

  h1 { font-size: var(--bannerTitle); line-height: 0.98; }
  h2 { font-size: var(--h2); }
  h3 { font-size: var(--h3); }
  h4 { font-size: var(--h4); }
  h5 { font-size: var(--h5); }
  h6 { font-size: var(--h6); }

  p {
    margin-top: 0.5rem;
    margin-bottom: 1rem;
    color: var(--bodyColor);
  }

  b, strong { font-weight: 700; color: var(--color-paper); }
  i { font-style: italic; }

  a {
    color: var(--color-paper);
    transition: color 0.3s var(--ease-power);
    cursor: pointer;

    &:hover,
    &:focus {
      color: var(--color-gold-500);
      text-decoration: none;
    }
  }

  ::selection { background: var(--color-gold-500); color: #1a1206; }

  :focus-visible {
    outline: 2px solid var(--color-gold-400);
    outline-offset: 4px;
    border-radius: 2px;
  }

  ::-webkit-scrollbar { width: 10px; }
  ::-webkit-scrollbar-track { background: var(--color-ink); }
  ::-webkit-scrollbar-thumb { background: var(--color-smoke); border-radius: 99px; }
  ::-webkit-scrollbar-thumb:hover { background: var(--color-gold-700); }

  .underline {
    text-decoration: underline;
    text-underline-position: under;
    text-decoration-color: rgba(231, 163, 56, 0.5);
    text-decoration-thickness: 0.125rem;
  }

  hr, ol, ul, blockquote {
    margin-top: calc(var(--sectionMargin) / 2);
    margin-bottom: calc(var(--sectionMargin) / 2);
  }

  hr {
    border: none;
    height: 1px;
    background-color: var(--color-line);
  }

  blockquote {
    font-size: var(--blockquote);
    font-family: var(--font-display);
    font-style: italic;
    margin-left: 0;
    border-left: 2px solid var(--color-gold-500);
    padding-left: var(--gap);
    color: var(--color-paper);
  }

  /* ── layout utilities (retained from the original starter) ── */
  .container {
    margin-left: auto;
    margin-right: auto;

    &__tight {
      max-width: 1320px;
    }
  }

  .section {
    margin-top: var(--sectionMargin);
    margin-bottom: var(--sectionMargin);
    padding: 0 var(--borderSpacing);

    &.section__padding {
      padding-top: var(--sectionMargin);
      padding-bottom: var(--sectionMargin);
    }
  }

  .intro__area {
    margin-bottom: calc(var(--gap) * 1.6);
    max-width: 780px;

    h2 {
      font-size: var(--text-title);
    }
  }

  .learn__more {
    margin-top: calc(var(--gap) * 1.5);
  }

  .feed {
    display: flex;
    flex-wrap: wrap;
    gap: var(--gap);
    margin-left: auto;
    margin-right: auto;
    max-width: 1320px;

    > * {
      flex: 0 0 100%;

      @media (min-width: 414px) {
        flex-basis: calc(50% - (calc(var(--gap) / 2)));
      }

      @media (min-width: 1024px) {
        flex-basis: calc(33.333% - 27px);
      }
    }
  }

  .contentimg {
    margin-top: var(--gap);
    margin-bottom: var(--gap);
  }

  .contentdescription {
    color: var(--bodyColor);
    font-style: italic;
    font-size: 0.813rem;
    text-align: center;
    margin-top: calc(var(--gap) / 2);
  }

  /* ── house helper classes ───────────────────────────────── */

  /* kicker label: amber, uppercase, clean — NO tick/mono */
  .eyebrow,
  .kicker {
    display: inline-block;
    font-family: var(--font-sans);
    font-size: var(--text-kicker);
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--color-gold-500);
    margin: 0;
  }

  /* big display heading */
  .headline {
    font-family: var(--font-display);
    font-size: var(--text-headline);
    font-weight: 600;
    line-height: 0.98;
    letter-spacing: -0.02em;
  }

  /* intro / lead paragraph */
  .lead {
    font-size: var(--text-lead);
    line-height: 1.5;
    color: var(--color-ash);
  }

  /* soft divider */
  .rule {
    height: 1px;
    width: 100%;
    background: var(--color-line);
    border: none;
    margin: 0;
  }

  /* cinematic amber duotone treatment for imagery */
  .duo {
    filter: sepia(0.7) saturate(1.5) hue-rotate(-18deg) brightness(0.7)
      contrast(1.05);
    transition: filter 0.6s var(--ease-expo), transform 0.8s var(--ease-expo);
  }
  .duo:hover {
    filter: saturate(1.05) brightness(0.92);
  }
`

export default GlobalStyle
